import { useMutation } from "@tanstack/react-query";
import {
  Translate,
  useDataProvider,
  useNotify,
  useRedirect,
  useRequireAccess,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { SimpleForm } from "@/components/admin/simple-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileFormToolbar, MobilePage } from "../layout/MobilePage";
import type { CrmDataProvider } from "../providers/types";
import type { SalesFormData } from "../types";
import { SalesInputs } from "./SalesInputs";
import { TemporaryPasswordDialog } from "./TemporaryPasswordDialog";

/**
 * Creates a user through `dataProvider.salesCreate`, which generates a
 * temporary password. That password is shown exactly once, in a dialog.
 */
export function SalesCreate() {
  // Non-admins are redirected to the access-denied page
  const { isPending: isAccessPending } = useRequireAccess({
    resource: "sales",
    action: "create",
  });
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [temporaryPassword, setTemporaryPassword] = useState<{
    name: string;
    password: string;
  } | null>(null);

  const { mutate } = useMutation({
    mutationKey: ["salesCreate"],
    mutationFn: async (data: SalesFormData) => dataProvider.salesCreate(data),
    onSuccess: (sale) => {
      notify("resources.sales.create.success");
      if (sale.temporaryPassword) {
        setTemporaryPassword({
          name: `${sale.first_name} ${sale.last_name}`,
          password: sale.temporaryPassword,
        });
        return;
      }
      redirect("list", "sales");
    },
    onError: (error: Error) => {
      notify(error?.message || "resources.sales.create.error", {
        type: "error",
      });
    },
  });

  const onSubmit: SubmitHandler<SalesFormData> = async (data) => {
    mutate(data);
  };

  if (isAccessPending) return null;

  const form = (
    <SimpleForm
      onSubmit={onSubmit as SubmitHandler<any>}
      defaultValues={{ administrator: false, disabled: false }}
      toolbar={isMobile ? <MobileFormToolbar /> : undefined}
    >
      <SalesInputs />
    </SimpleForm>
  );

  const dialog = (
    <TemporaryPasswordDialog
      open={temporaryPassword != null}
      name={temporaryPassword?.name ?? ""}
      password={temporaryPassword?.password ?? ""}
      onClose={() => {
        setTemporaryPassword(null);
        redirect("list", "sales");
      }}
    />
  );

  if (isMobile) {
    return (
      <MobilePage
        title={translate("resources.sales.create.title")}
        backTo="/sales"
      >
        {form}
        {dialog}
      </MobilePage>
    );
  }

  return (
    <div className="max-w-lg w-full mx-auto mt-8">
      <Card>
        <CardHeader>
          <CardTitle>
            <Translate i18nKey="resources.sales.create.title" />
          </CardTitle>
        </CardHeader>
        <CardContent>{form}</CardContent>
      </Card>
      {dialog}
    </div>
  );
}
